import * as React from 'react';
import { IntegrationType, IssueBuild, IssueSource, IssueSourceType, IssueState } from '@/types';
import { useSelector } from 'react-redux';
import { getIntegrationTypeForAppStoreType } from '@/helpers';
import { displayTextForIssueSourceType } from '@/helpers/displayHelpers';
import { BuildSummary, getBuildsForVersion } from '@/api';
import { RootState } from '@/reducers/reducers';
import { selectAppStoreType, selectAuthCredentials, selectDemoIssuesForApp } from '@/selectors/selectors';
import { getDemoBuildsForVersion } from '@/testData/issues';
import CIAppStoreBuildComponent from '@/components/CIAppStoreBuildComponent';
import IntegrationIcon from '@/components/icons/IntegrationIcon';
import runwayLogo from '../../../assets/integration-runway.svg';
import { Components } from 'react-markdown';
import { antGrey } from '@/components/styles/globalStyles';

// Descriptions pulled from tickets embed screenshots at their native size, which overflow the drawer.
export const DESCRIPTION_MARKDOWN_COMPONENTS: Components = {
    img: ({ node: _node, ...props }) => (
        <img
            {...props}
            style={{ maxWidth: '100%', height: 'auto', borderRadius: 4, border: `1px solid ${antGrey.grey5}` }}
        />
    ),
};

export const CLOSED_ISSUE_STATES = [
    IssueState.FixMerged,
    IssueState.FixRejected,
    IssueState.NotAPriority,
    IssueState.ResolvedExternally,
];

export const IssueSourceIcon: React.FC<{
    source: IssueSource | undefined;
    appId: string;
    size?: number;
}> = ({ source, appId, size = 16 }) => {
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, appId));

    if (source?.type === IssueSourceType.Runway) {
        return <img src={runwayLogo} alt="Team" width={size} height={size} />;
    }
    const derivedIntegrationType =
        source?.type === IssueSourceType.UserReviews && appStoreType ? getIntegrationTypeForAppStoreType(appStoreType)
        : source?.type === IssueSourceType.BetaFeedback ? IntegrationType.TestFlight
        : null;
    return <IntegrationIcon integrationId={derivedIntegrationType ?? source?.integrationType} size={size} />;
};

export const usePendingIssueTransition = (isTransitioning: boolean) => {
    const [pendingTransition, setPendingTransition] = React.useState<IssueState | null>(null);
    React.useEffect(() => {
        if (!isTransitioning) {
            setPendingTransition(null);
        }
    }, [isTransitioning]);
    return { pendingTransition, setPendingTransition };
};

export const issueSourceDisplayText = (source: IssueSource): string =>
    source.displayText || displayTextForIssueSourceType(source.type);

export const sourceOptionLabel = (
    source: IssueSource | undefined,
    appId: string,
    text: string,
    suffix?: React.ReactNode,
): React.ReactNode => (
    <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, maxWidth: '100%' }}>
        <span style={{ flexShrink: 0, lineHeight: 0 }}>
            <IssueSourceIcon source={source} appId={appId} />
        </span>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{text}</span>
        {suffix && (
            <span style={{ marginLeft: 'auto', flexShrink: 0, color: antGrey.grey7, fontSize: 12 }}>{suffix}</span>
        )}
    </span>
);

export const useBuildsForVersion = (orgId: string, appId: string, version?: string) => {
    const auth = useSelector((state: RootState) => selectAuthCredentials(state));
    const demoIssues = useSelector((state: RootState) => selectDemoIssuesForApp(state, appId));
    const [builds, setBuilds] = React.useState<BuildSummary[]>([]);
    const [buildsLoading, setBuildsLoading] = React.useState(false);

    React.useEffect(() => {
        let cancelled = false;
        if (demoIssues) {
            setBuilds(version ? (getDemoBuildsForVersion(demoIssues, version) as BuildSummary[]) : []);
        } else if (version) {
            setBuilds([]);
            setBuildsLoading(true);
            getBuildsForVersion(orgId, appId, version, auth)
                .then((res) => !cancelled && setBuilds(res.builds ?? []))
                .catch(() => !cancelled && setBuilds([]))
                .finally(() => !cancelled && setBuildsLoading(false));
        } else {
            setBuilds([]);
        }
        return () => {
            cancelled = true;
        };
    }, [version, orgId, appId, auth, demoIssues]);

    return { builds, buildsLoading };
};

export const buildSelectOptions = (builds: IssueBuild[]) =>
    builds.map((b) => ({
        value: b.identifier || (b.appStoreBuild ?? ''),
        searchLabel: [b.identifier, b.appStoreBuild].filter(Boolean).join(' '),
        label: (
            <span style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                <CIAppStoreBuildComponent
                    ciBuildIdentifier={b.identifier}
                    ciBuildIntegrationId={b.integrationId as IntegrationType}
                    appStoreBuildIdentifier={b.appStoreBuild}
                    appStoreBuildIntegrationId={b.appStoreIntegrationId as IntegrationType}
                    isAppStoreBuildProcessed={b.appStoreBuildIsProcessed ?? true}
                    strong={false}
                />
            </span>
        ),
    }));

export const sortUsersBySuggestedFirst = <T extends { id: string }>(users: T[], suggestedUserIds?: string[]): T[] => {
    if (!suggestedUserIds?.length) {
        return users;
    }
    const rank = (user: T) => {
        const index = suggestedUserIds.indexOf(user.id);
        return index === -1 ? suggestedUserIds.length : index;
    };
    return [...users].sort((a, b) => rank(a) - rank(b));
};
